// Politica unica de redaccion y acotado para cuanto entra a los buffers de
// depuracion en dispositivo (logTracker y networkTracker).
//
// Vive aparte de los dos a proposito: networkTracker ya importa de logTracker, asi
// que dejar `summarize` en cualquiera de los dos y consumirlo desde el otro cerraria
// un ciclo de imports. Ademas, tener la politica en un solo lugar evita que una
// puerta quede protegida y la otra no.
//
// Dos invariantes, y ninguna es opcional:
//   1. Nada sensible entra al buffer. El boton de copiar del LogTracker manda el buffer
//      al portapapeles del usuario, asi que un token ahi es un token afuera.
//   2. Nada se copia crudo. Los payloads de ocurrencias traen base64 de fotos: un
//      solo objeto puede pesar decenas de MB y asignarlo entero en un handheld de
//      gama baja es lo que dispara al low memory killer.

export const MAX_STRING = 200
export const MAX_DEPTH = 3
export const MAX_KEYS = 40
export const MAX_ARRAY_ITEMS = 3

// Claves cuyo VALOR nunca entra al buffer. No es paranoia de mas: el login pega por
// la misma instancia de axios (users.authenticate, authenticateGQL, graphqlToken) con
// { client_id, client_secret, username, password } en el body, y la respuesta trae
// access_token y refresh_token. Se compara por substring, en minusculas.
export const REDACTED_KEYS = [
  'password', 'passwd', 'pwd', 'secret', 'client_secret',
  'token', 'authorization', 'credential', 'firebase_id'
]

export function isSensitiveKey (key) {
  const name = String(key).toLowerCase()
  return REDACTED_KEYS.some(needle => name.includes(needle))
}

/**
 * Resumen acotado de un valor cualquiera. Corta strings largos, arreglos y
 * profundidad para que el resultado sea imprimible sin cargar el payload entero,
 * y redacta por nombre de clave en el camino.
 */
export function summarize (value, depth = 0) {
  if (value === null || value === undefined) return value
  const type = typeof value

  if (type === 'string') {
    return value.length > MAX_STRING
      ? `${value.slice(0, MAX_STRING)}...(${value.length} chars)`
      : value
  }
  if (type === 'number' || type === 'boolean') return value
  if (type === 'function') return '[function]'

  if (Array.isArray(value)) {
    if (depth >= MAX_DEPTH) return `[Array(${value.length})]`
    const head = value.slice(0, MAX_ARRAY_ITEMS).map(item => summarize(item, depth + 1))
    if (value.length > MAX_ARRAY_ITEMS) head.push(`...+${value.length - MAX_ARRAY_ITEMS} mas`)
    return head
  }

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return `[Blob ${value.size} bytes, ${value.type || 'sin type'}]`
  }
  if (typeof File !== 'undefined' && value instanceof File) {
    return `[File ${value.name}, ${value.size} bytes]`
  }
  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
    return `[ArrayBuffer ${value.byteLength} bytes]`
  }
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const fields = {}
    try {
      for (const [key, item] of value.entries()) {
        fields[key] = isSensitiveKey(key) ? '[redactado]' : summarize(item, depth + 1)
      }
    } catch (error) {
      return `[FormData no inspeccionable: ${error.message}]`
    }
    return fields
  }

  if (type === 'object') {
    if (depth >= MAX_DEPTH) return '{...}'
    const out = {}
    const keys = Object.keys(value)
    for (const key of keys.slice(0, MAX_KEYS)) {
      out[key] = isSensitiveKey(key) ? '[redactado]' : summarize(value[key], depth + 1)
    }
    if (keys.length > MAX_KEYS) out['...'] = `+${keys.length - MAX_KEYS} keys mas`
    return out
  }

  return String(value)
}
